import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { AlertTriangle, Check, CheckCircle2, Clock, Inbox, Lock, Pencil, Send, X } from "lucide-react"
import type { User } from "@pishgam/contracts"

import { ApiFailure, api } from "../api"
import { ConfirmDialog, EmptyState, listKeys, LoadingCard, messageFor, Page } from "../shared"
import { fa, STATE_LABEL, type AllocationState, type Cycle, type Period, type Step } from "../process"
import { ProcessTimeline } from "@/components/process-timeline"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

type Status = {
  period: Period
  periodLabel: string
  cycle: Cycle
  closed: { at: string } | null
  counts: { staff: number; notSubmitted: number; awaitingDivision: number; awaitingUnits: number; approved: number }
  people: Array<{ id: number; nameFa: string; division: string | null; divisionHead: string | null; allocationId: number | null; state: AllocationState; late: boolean }>
  divisions: Array<{ headId: number | null; division: string; head: string | null; pending: number }>
  units: Array<{ id: number; nameFa: string; head: string | null; pending: number; revenue: "entered" | "missing" }>
  payroll: { rows: number; state: "missing" | "copied" | "entered" | "approved" }
  costsCopiedAt: string | null
  otherCostsMillion: string
  unallocated: Array<{ nameFa: string; amountMillion: string }>
}

const STATE_TONE: Record<AllocationState, string> = {
  missing: "bg-muted text-muted-foreground", draft: "bg-amber-100 text-amber-900", rejected: "bg-destructive/10 text-loss",
  submitted: "bg-blue-100 text-blue-900", div_approved: "bg-teal-100 text-teal-900", approved: "bg-emerald-100 text-emerald-900",
}
// Every state carries an icon next to its label: status is never color-alone.
const STATE_ICON: Record<AllocationState, typeof Clock> = {
  missing: Clock, draft: Pencil, rejected: X, submitted: Send, div_approved: Inbox, approved: Check,
}

const TABS = [["people", "پرسنل"], ["approvals", "تأییدها"], ["costs", "هزینه‌ها"]] as const
type Tab = (typeof TABS)[number][0]

export function CloseMonitorPage({ user }: { user: User }) {
  const client = useQueryClient()
  const [tab, setTab] = useState<Tab>("people")
  const [query, setQuery] = useState("")
  const [stateFilter, setStateFilter] = useState<"all" | "open" | "late">("all")
  const [forceMessage, setForceMessage] = useState<string | null>(null)
  const [reopenOpen, setReopenOpen] = useState(false)
  const status = useQuery({ queryKey: ["process-status"], queryFn: () => api<Status>("/process/status") })
  const close = useMutation({
    mutationFn: (force: boolean) => api("/process/close", { method: "POST", body: JSON.stringify({ ...status.data!.period, force }) }),
    onSuccess: () => { setForceMessage(null); return Promise.all(["process-status", "process-tasks"].map((key) => client.invalidateQueries({ queryKey: [key] }))) },
    onError: (error) => { if (error instanceof ApiFailure && error.status === 409) { setForceMessage(error.message); close.reset() } },
  })
  const reopen = useMutation({
    mutationFn: () => api("/process/reopen", { method: "POST", body: JSON.stringify(status.data!.period) }),
    onSuccess: () => { setReopenOpen(false); return Promise.all(["process-status", "process-tasks"].map((key) => client.invalidateQueries({ queryKey: [key] }))) },
  })

  if (status.isError) return (
    <Page title="پایش بستن ماه">
      <Card><CardContent className="grid gap-3 pt-6">
        <p role="alert" className="text-sm text-loss">{messageFor(status.error)}</p>
        <div><Button variant="outline" onClick={() => status.refetch()}>تلاش مجدد</Button></div>
      </CardContent></Card>
    </Page>
  )
  if (!status.data) return <Page title="پایش بستن ماه"><LoadingCard /></Page>
  const s = status.data
  const d = s.cycle.deadlines
  const open = s.counts.notSubmitted + s.counts.awaitingDivision + s.counts.awaitingUnits
  const missingRevenue = s.units.filter((unit) => unit.revenue === "missing").length
  const payrollApproved = s.payroll.state === "approved"
  const trimmed = query.trim()
  const visiblePeople = [...s.people]
    .sort((a, b) => order(a.state) - order(b.state))
    .filter((person) => (stateFilter === "open" ? person.state !== "approved" : stateFilter === "late" ? person.late : true))
    .filter((person) => !trimmed || person.nameFa.includes(trimmed) || (person.division ?? "").includes(trimmed))
  const closedAt = s.closed ? new Date(s.closed.at).toLocaleDateString("fa-IR", { day: "numeric", month: "long" }) : null
  const doneSteps: Step[] = [
    ...(s.counts.notSubmitted === 0 ? ["allocation" as const] : []),
    ...(payrollApproved ? ["payroll" as const] : []),
    ...(s.counts.awaitingDivision === 0 ? ["division" as const] : []),
    ...(s.counts.awaitingUnits === 0 ? ["unit" as const] : []),
  ]

  return (
    <Page
      title={`پایش بستن حساب ${fa(s.periodLabel)}`}
      description={s.closed ? `این دوره ${closedAt ? `در ${fa(closedAt)} ` : ""}بسته شد.` : `باید تا ${fa(d.close.label)} بسته شود.${open ? ` ${fa(open)} مورد هنوز باز است.` : " همه موارد آماده بستن است."}`}
      actions={s.closed ? (
        <>
          <Badge className="h-7 gap-1 bg-success px-3 text-sm"><Lock className="size-3.5" aria-hidden /> بسته شده</Badge>
          {user.capabilities.admin && <Button variant="outline" disabled={reopen.isPending} onClick={() => setReopenOpen(true)}><AlertTriangle className="size-4" aria-hidden /> بازگشایی دوره</Button>}
        </>
      ) : (
        <Button disabled={close.isPending} onClick={() => close.mutate(false)}><Lock className="size-4" aria-hidden /> {close.isPending ? "در حال بستن…" : "بستن دوره"}</Button>
      )}
    >
      {close.error && <p role="alert" className="rounded-lg border border-loss/40 bg-loss/5 px-4 py-3 text-sm text-loss">{messageFor(close.error)}</p>}
      <Card><CardContent><ProcessTimeline cycle={s.cycle} closed={Boolean(s.closed)} doneSteps={doneSteps} /></CardContent></Card>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StepCard title="ثبت نسبت کاری" value={`${fa(s.counts.staff - s.counts.notSubmitted)} از ${fa(s.counts.staff)}`} late={s.counts.notSubmitted > 0 && d.allocation.passed} note={s.counts.notSubmitted ? `${fa(s.counts.notSubmitted)} نفر ارسال نکرده‌اند` : "همه ارسال کرده‌اند"} />
        <StepCard title="تأیید مدیر واحد" value={fa(s.counts.awaitingDivision)} late={s.counts.awaitingDivision > 0 && d.division.passed} note={s.counts.awaitingDivision ? "منتظر تأیید" : "موردی در انتظار نیست"} />
        <StepCard title="تأیید کسب‌وکار" value={fa(s.counts.awaitingUnits)} late={s.counts.awaitingUnits > 0 && d.unit.passed} note={s.counts.awaitingUnits ? "منتظر تأیید نهایی" : "موردی در انتظار نیست"} />
        <StepCard title="حقوق، هزینه‌ها و درآمد"
          value={payrollApproved ? "حقوق تأیید شد" : s.payroll.state === "copied" ? "کپی خودکار" : "تأیید نشده"}
          late={(!payrollApproved || missingRevenue > 0) && d.payroll.passed}
          note={missingRevenue ? `درآمد ${fa(missingRevenue)} کسب‌وکار ثبت نشده` : "درآمد همه کسب‌وکارها ثبت شده"} />
      </div>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <div className="inline-flex rounded-lg border bg-muted p-1" role="tablist" aria-label="نمای پایش" onKeyDown={(event) => listKeys(event, "tab", (index) => setTab(TABS[index]![0]))}>
            {TABS.map(([value, label]) => (
              <button key={value} type="button" role="tab" id={`monitor-tab-${value}`} aria-selected={tab === value} aria-controls={`monitor-panel-${value}`} tabIndex={tab === value ? 0 : -1} onClick={() => setTab(value)}
                className={cn("rounded-md px-4 py-1.5 text-sm font-medium focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none", tab === value ? "bg-background shadow-sm" : "text-muted-foreground")}>{label}</button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {tab === "people" && (
            <div role="tabpanel" id="monitor-panel-people" aria-labelledby="monitor-tab-people">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="جست‌وجوی نام یا واحد…" aria-label="جست‌وجوی پرسنل" className="h-8 w-52 text-sm" />
                <div className="inline-flex rounded-lg border bg-muted p-0.5" role="group" aria-label="پالایش وضعیت">
                  {([["all", "همه"], ["open", "باز"], ["late", "دیرکرد"]] as const).map(([value, label]) => (
                    <button key={value} type="button" aria-pressed={stateFilter === value} onClick={() => setStateFilter(value)}
                      className={cn("rounded-md px-3 py-1 text-xs font-medium focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none", stateFilter === value ? "bg-background shadow-sm" : "text-muted-foreground")}>{label}</button>
                  ))}
                </div>
                <span className="ms-auto text-xs text-muted-foreground">{fa(visiblePeople.length)} نفر</span>
              </div>
              <Table>
                <TableHeader className="sticky top-0 bg-background"><TableRow><TableHead className="text-right">نام</TableHead><TableHead className="text-right">واحد</TableHead><TableHead className="text-right">وضعیت</TableHead><TableHead><span className="sr-only">اقدام</span></TableHead></TableRow></TableHeader>
                <TableBody>
                  {visiblePeople.length ? visiblePeople.map((person) => {
                    const Icon = STATE_ICON[person.state]
                    return (
                      <TableRow key={person.id}>
                        <TableCell className="font-medium">{person.nameFa}</TableCell>
                        <TableCell className="text-muted-foreground">{person.division ?? "—"}</TableCell>
                        <TableCell>
                          <span className={cn("inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs", STATE_TONE[person.state])}><Icon className="size-3" aria-hidden />{STATE_LABEL[person.state]}</span>
                          {person.late && <span className="ms-2 inline-flex items-center gap-1 text-xs text-loss"><AlertTriangle className="size-3" aria-hidden /> دیرکرد</span>}
                        </TableCell>
                        <TableCell className="text-left">{person.allocationId && <Button variant="ghost" size="sm" asChild><Link to={`/allocations/${person.allocationId}`}>جزئیات</Link></Button>}</TableCell>
                      </TableRow>
                    )
                  }) : (
                    <TableRow><TableCell colSpan={4}><EmptyState text={trimmed || stateFilter !== "all" ? "کسی با این مشخصات پیدا نشد." : "هنوز کسی ثبت نشده است."} /></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
          {tab === "approvals" && (
            <div role="tabpanel" id="monitor-panel-approvals" aria-labelledby="monitor-tab-approvals" className="grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="mb-2 text-sm font-semibold">مدیران واحد</h3>
                {s.divisions.length ? s.divisions.map((row) => (
                  <div key={row.division} className="flex items-center justify-between border-b py-2 text-sm last:border-0">
                    <span>{row.head ?? <span className="text-loss">بدون مدیر</span>} <span className="text-muted-foreground">({row.division})</span></span>
                    <Badge variant={d.division.passed ? "destructive" : "secondary"} className="gap-1">
                      {d.division.passed ? <AlertTriangle className="size-3" aria-hidden /> : <Clock className="size-3" aria-hidden />}{fa(row.pending)} مورد
                    </Badge>
                  </div>
                )) : <p className="text-sm text-muted-foreground">موردی منتظر مدیران واحد نیست.</p>}
              </div>
              <div>
                <h3 className="mb-2 text-sm font-semibold">مدیران کسب‌وکار</h3>
                {s.units.filter((unit) => unit.pending).length ? s.units.filter((unit) => unit.pending).map((unit) => (
                  <div key={unit.id} className="flex items-center justify-between border-b py-2 text-sm last:border-0">
                    <span>{unit.head ?? <span className="text-loss">بدون مدیر</span>} <span className="text-muted-foreground">({unit.nameFa})</span></span>
                    <Badge variant={d.unit.passed ? "destructive" : "secondary"} className="gap-1">
                      {d.unit.passed ? <AlertTriangle className="size-3" aria-hidden /> : <Clock className="size-3" aria-hidden />}{fa(unit.pending)} مورد
                    </Badge>
                  </div>
                )) : <p className="text-sm text-muted-foreground">موردی منتظر مدیران کسب‌وکار نیست.</p>}
              </div>
            </div>
          )}
          {tab === "costs" && (
            <div role="tabpanel" id="monitor-panel-costs" aria-labelledby="monitor-tab-costs" className="grid gap-4">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 text-sm">
                <dl className="grid gap-1">
                  <div className="flex gap-2"><dt className="text-muted-foreground">حقوق {fa(s.periodLabel)}:</dt><dd className="font-medium">{payrollApproved ? "تأیید شد و بین کسب‌وکارها تقسیم شد" : s.payroll.state === "missing" ? "ثبت نشده" : s.payroll.state === "copied" ? "از ماه قبل کپی شد؛ منتظر تأیید" : "ثبت شده؛ منتظر تأیید"}</dd></div>
                  <div className="flex gap-2"><dt className="text-muted-foreground">سایر هزینه‌ها:</dt><dd className="font-medium">{fa(Number(s.otherCostsMillion).toLocaleString("fa-IR"))} میلیون ریال</dd></div>
                </dl>
                <Button variant="outline" size="sm" asChild><Link to="/payroll">حقوق و سایر هزینه‌ها</Link></Button>
              </div>
              {s.unallocated.length > 0 && (
                s.unallocated.length > 5 ? (
                  <details className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    <summary className="cursor-pointer font-medium">حقوق بدون نسبت کاری تأییدشده ({fa(s.unallocated.length)} نفر) — به کسب‌وکارها تقسیم نمی‌شود</summary>
                    <p className="mt-1">{s.unallocated.map((row) => `${row.nameFa} (${Number(row.amountMillion).toLocaleString("fa-IR")})`).join("، ")}</p>
                  </details>
                ) : (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    <p className="flex items-center gap-1.5 font-medium"><AlertTriangle className="size-4" aria-hidden /> حقوق بدون نسبت کاری تأییدشده (به کسب‌وکارها تقسیم نمی‌شود):</p>
                    <p className="mt-1">{s.unallocated.map((row) => `${row.nameFa} (${Number(row.amountMillion).toLocaleString("fa-IR")})`).join("، ")}</p>
                  </div>
                )
              )}
              <h3 className="text-sm font-semibold">درآمد محقق‌شده {fa(s.periodLabel)}</h3>
              {s.units.map((unit) => (
                <div key={unit.id} className="flex items-center justify-between border-b py-2 text-sm last:border-0">
                  <span>{unit.nameFa}</span>
                  {unit.revenue === "entered"
                    ? <Badge className="gap-1 bg-success"><CheckCircle2 className="size-3" aria-hidden /> ثبت شده</Badge>
                    : <Badge variant="destructive" className="gap-1"><X className="size-3" aria-hidden /> ثبت نشده</Badge>}
                </div>
              ))}
              <Button variant="outline" className="w-fit" asChild><Link to="/finance/entry">ورود درآمدها</Link></Button>
            </div>
          )}
          <ConfirmDialog
            open={forceMessage !== null} onOpenChange={(next) => { if (!next) setForceMessage(null) }}
            title="بستن دوره با موارد باز" description={`${forceMessage ?? ""}\nبا این حال دوره بسته شود؟ موارد باز دیگر قابل تغییر نخواهند بود.`}
            confirmLabel="بستن دوره" danger pending={close.isPending} onConfirm={() => close.mutate(true)}
          />
          <ConfirmDialog
            open={reopenOpen} onOpenChange={setReopenOpen}
            title="بازگشایی دوره" description="دوره دوباره باز شود؟ گزارش‌ها و وضعیت تأییدها به حالت باز برمی‌گردد و بستن بعدی باید از اول انجام شود."
            confirmLabel="بازگشایی" danger pending={reopen.isPending} onConfirm={() => reopen.mutate()}
          />
        </CardContent>
      </Card>
    </Page>
  )
}

function StepCard({ title, value, note, late }: { title: string; value: string; note: string; late: boolean }) {
  return (
    <div className={cn("grid gap-1 rounded-xl border bg-card px-4 py-3.5", late && "border-loss/40 bg-loss/[0.03]")}>
      <p className="flex items-center gap-1.5 text-sm text-muted-foreground">{late && <AlertTriangle className="size-3.5 text-loss" aria-label="دیرکرد" />}{title}</p>
      <p className="text-xl font-semibold tabular-nums">{value}</p>
      <p className={cn("text-xs", late ? "font-medium text-loss" : "text-muted-foreground")}>{note}</p>
    </div>
  )
}

const ORDER: AllocationState[] = ["missing", "rejected", "draft", "submitted", "div_approved", "approved"]
const order = (state: AllocationState) => ORDER.indexOf(state)
